import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import NorthstoneLogo from '../components/NorthstoneLogo';
import { validateOTP } from '../utils/otpValidation';

const { useState } = React;

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showOtpStep, setShowOtpStep] = useState(false);
  const [tempUserCredential, setTempUserCredential] = useState(null);

  async function handleInitialSubmit(e) {
    e.preventDefault();
    try {
      setError('');
      setLoading(true);
      
      // First login the user
      const userCredential = await login(email, password);
      setTempUserCredential(userCredential);
      setShowOtpStep(true);
      
    } catch (error) {
      console.error('Login error:', error);
      setError('Failed to sign in. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  }

  async function handleOtpSubmit(e) {
    e.preventDefault();
    try {
      setError('');
      setLoading(true);

      if (!validateOTP(otp)) {
        throw new Error('Invalid OTP');
      }

      // Get user role from Firestore
      const userDoc = await getDoc(doc(db, 'users', tempUserCredential.user.uid));
      const userData = userDoc.data();
      
      // Redirect based on role
      if (userData.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      if (error.message === 'Invalid OTP') {
        setError('Invalid OTP. Please try again.');
      } else {
        setError('Failed to verify OTP. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  // Illustration icons for the visual side
  const IllustrationElements = () => (
    <div className="relative h-full w-full">
      {/* Main illustration */}
      <div className="absolute inset-0 flex items-center justify-center">
        <svg className="w-3/4 h-auto text-white opacity-95" viewBox="0 0 1119 699" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M292.5 213C292.5 227.5 264 236.5 230 236.5C196 236.5 168 227.5 168 213C168 198.5 196 189.5 230 189.5C264 189.5 292.5 198.5 292.5 213Z" fill="currentColor" />
          <path d="M292.5 213C292.5 227.5 264 236.5 230 236.5C196 236.5 168 227.5 168 213M292.5 213C292.5 198.5 264 189.5 230 189.5C196 189.5 168 198.5 168 213M292.5 213V282M168 213V282M292.5 282C292.5 296.5 264 305.5 230 305.5C196 305.5 168 296.5 168 282M292.5 282C292.5 267.5 264 258.5 230 258.5C196 258.5 168 267.5 168 282" stroke="currentColor" strokeWidth="3" />
          <path d="M821.5 520C821.5 534.5 793 543.5 759 543.5C725 543.5 697 534.5 697 520C697 505.5 725 496.5 759 496.5C793 496.5 821.5 505.5 821.5 520Z" fill="currentColor" />
          <path d="M821.5 520C821.5 534.5 793 543.5 759 543.5C725 543.5 697 534.5 697 520M821.5 520C821.5 505.5 793 496.5 759 496.5C725 496.5 697 505.5 697 520M821.5 520V589M697 520V589M821.5 589C821.5 603.5 793 612.5 759 612.5C725 612.5 697 603.5 697 589M821.5 589C821.5 574.5 793 565.5 759 565.5C725 565.5 697 574.5 697 589" stroke="currentColor" strokeWidth="3" />
          <rect x="416.5" y="249.5" width="281" height="200" rx="3.5" fill="currentColor" stroke="currentColor" strokeWidth="5" />
          <rect x="266" y="23" width="602" height="653" rx="20" stroke="currentColor" strokeWidth="6" />
          <path d="M309 385L382 308L433.5 385H309Z" fill="currentColor" />
          <path d="M496 385L512 361L529 385H496Z" fill="currentColor" />
          <path d="M562 385L617.5 308L673 385H562Z" fill="currentColor" />
          <path d="M309 141L372.5 72L436 141H309Z" fill="currentColor" />
          <path d="M496 141L552 72L608 141H496Z" fill="currentColor" />
          <path d="M682 141L710 110L738 141H682Z" fill="currentColor" />
          <path d="M309 249.5H697" stroke="currentColor" strokeWidth="5" />
          <path d="M309 449.5H697" stroke="currentColor" strokeWidth="5" />
          <path d="M416.5 142V449" stroke="currentColor" strokeWidth="5" />
          <path d="M592.5 142V249" stroke="currentColor" strokeWidth="5" />
          <path d="M592.5 449V556" stroke="currentColor" strokeWidth="5" />
          <path d="M309 556.5H697" stroke="currentColor" strokeWidth="5" />
          <circle cx="138" cy="563" r="30" fill="currentColor" />
          <circle cx="968" cy="139" r="30" fill="currentColor" />
          <path d="M64 343L137 270L210 343L137 416L64 343Z" stroke="currentColor" strokeWidth="3" />
          <path d="M908 412L981 339L1054 412L981 485L908 412Z" stroke="currentColor" strokeWidth="3" />
        </svg>
      </div>
      
      {/* Floating elements */}
      <div className="absolute top-1/4 left-1/4 w-12 h-12 bg-primary-400 rounded-lg opacity-70 animate-float"></div>
      <div className="absolute bottom-1/4 right-1/3 w-16 h-16 bg-secondary-400 rounded-full opacity-70 animate-float animation-delay-1000"></div>
      <div className="absolute top-2/3 left-1/3 w-10 h-10 bg-accent-400 rounded-lg opacity-70 animate-float animation-delay-2000"></div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-white">
      {/* Left side - Visual */}
      <div className="hidden md:block md:w-1/2 bg-gradient-to-br from-primary-600 to-secondary-700 text-white relative overflow-hidden">
        <IllustrationElements />
        <div className="absolute bottom-8 left-8 right-8">
          <h2 className="text-3xl font-serif font-bold">Northstone Secure Portal</h2>
          <p className="mt-2 text-primary-100">Connecting you to what matters most.</p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="md:w-1/2 flex flex-col">
        <main className="flex-grow flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
          <div className="w-full max-w-md">
            <div className="flex justify-center md:justify-start">
              <NorthstoneLogo className="h-12 w-auto text-primary-600" />
            </div>
            
            <h2 className="mt-6 text-3xl font-serif font-bold text-gray-900">
              {showOtpStep ? 'Verify OTP' : 'Welcome Back'}
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {showOtpStep ? 'Enter the OTP to complete sign in' : 'Sign in to access your account'}
            </p>

            {error && (
              <div className="mt-6 bg-red-50 border-l-4 border-red-400 p-4 rounded-lg animate-fadeIn" role="alert">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-8">
              {!showOtpStep ? (
                <form className="space-y-6" onSubmit={handleInitialSubmit}>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      Email address
                    </label>
                    <div className="mt-1 relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                          <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                        </svg>
                      </div>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="appearance-none block w-full pl-10 pr-4 py-3 rounded-xl border 
                          border-gray-200 focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 
                          transition-colors"
                        placeholder="you@example.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                      Password
                    </label>
                    <div className="mt-1 relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <input
                        id="password"
                        name="password"
                        type="password"
                        autoComplete="current-password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="appearance-none block w-full pl-10 pr-4 py-3 rounded-xl border 
                          border-gray-200 focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 
                          transition-colors"
                        placeholder="••••••••"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <input
                        id="remember-me"
                        name="remember-me"
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500/20 border-gray-300 rounded"
                      />
                      <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                        Remember me
                      </label>
                    </div>

                    <div className="text-sm">
                      <a href="#" className="font-medium text-primary-600 hover:text-primary-700 transition-colors">
                        Forgot your password?
                      </a>
                    </div>
                  </div>

                  <div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full flex justify-center py-3 px-4 border border-transparent 
                        rounded-xl shadow-sm text-sm font-medium text-white bg-gradient-to-r from-primary-600 to-secondary-600 
                        hover:from-primary-700 hover:to-secondary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 
                        focus:ring-primary-500 transition-all"
                    >
                      {loading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Signing in...
                        </>
                      ) : (
                        'Continue'
                      )}
                    </button>
                  </div>
                </form>
              ) : (
                <form className="space-y-6" onSubmit={handleOtpSubmit}>
                  <div>
                    <label htmlFor="otp" className="block text-sm font-medium text-gray-700">
                      One-Time Password (OTP)
                    </label>
                    <div className="mt-1 relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <input
                        id="otp"
                        name="otp"
                        type="text"
                        required
                        maxLength="6"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="appearance-none block w-full pl-10 pr-4 py-3 rounded-xl border 
                          border-gray-200 focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 
                          transition-colors text-center tracking-[0.5em] font-mono"
                        placeholder="000000"
                      />
                    </div>
                    <p className="mt-2 text-sm text-gray-500">
                      Enter the 6-digit code from your authenticator app
                    </p>
                  </div>

                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowOtpStep(false);
                        setOtp('');
                        setError('');
                      }}
                      className="flex-1 py-3 px-4 border border-gray-200 rounded-xl text-gray-700
                        hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 
                        focus:ring-primary-500 transition-all"
                      disabled={loading}
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={loading || otp.length !== 6}
                      className="flex-1 flex justify-center py-3 px-4 border border-transparent 
                        rounded-xl shadow-sm text-sm font-medium text-white bg-gradient-to-r 
                        from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 
                        focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 
                        transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Verifying...
                        </>
                      ) : (
                        'Verify & Sign in'
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </main>
        
        <footer className="py-4 text-center">
          <p className="text-xs text-gray-500">
            © 2025 Northstone. All rights reserved.
          </p>
        </footer>
      </div>
    </div>
  );
}